import React from 'react';
import { Skeleton } from '@mui/material';
import { MuiPaper } from '@ace/ace-ui-web';
import styles from './SkeletonEcommSalesPerfGrid.module.css';

interface SkeletonEcommSalesPerfGridProps {
  /**
   * Number of main category rows to render
   * @default 3
   */
  categoryCount?: number;
  /**
   * Number of sub-category rows per main category
   * @default 3
   */
  subCategoryCount?: number;
}

/**
 * SkeletonEcommSalesPerfGrid Component - Displays loading skeleton for E-commerce Sales Performance Grid
 * Mimics the hierarchical structure with main categories and sub-categories
 * Matches the sales performance grid layout with multiple metric columns
 */
const SkeletonEcommSalesPerfGrid: React.FC<SkeletonEcommSalesPerfGridProps> = ({
  categoryCount = 3,
  subCategoryCount = 3,
}) => {
  const columnHeaders = [
    '', // Empty first column for row labels
    'This Year',
    'Peer Group Rank',
    '80% Benchmark',
    'Last Year',
    'Change (Over Last Year)',
    'Change (Over Last Year) %',
    'Peer Group Rank',
    '80% Benchmark',
  ];

  return (
    <MuiPaper
      elevation={2}
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        margin: '0 auto',
      }}>
      <div className={styles.container}>
        {/* Sales Header */}
        <div className={styles.salesHeader}>
          <Skeleton
            variant="text"
            width={60}
            height={24}
            sx={{ margin: '0 auto', backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
          />
        </div>

        {/* Table Container */}
        <div className={styles.tableContainer}>
          {/* Column Headers */}
          <div className={styles.tableHeader}>
            {columnHeaders.map((header, index) => (
              <div
                key={`header-${index}`}
                className={styles.headerCell}>
                {header && (
                  <Skeleton
                    variant="text"
                    width={index === 0 ? '60%' : '70%'}
                    height={18}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Table Body with Categories and Sub-categories */}
          {[...Array(categoryCount)].map((_, categoryIndex) => (
            <React.Fragment key={`category-${categoryIndex}`}>
              {/* Main Category Row */}
              <div className={`${styles.tableRow} ${styles.categoryRow}`}>
                {/* Category Name */}
                <div className={styles.tableCellLabel}>
                  <Skeleton
                    variant="text"
                    width="55%"
                    height={16}
                  />
                </div>
                {/* Data columns */}
                {[...Array(8)].map((_, colIndex) => (
                  <div
                    key={`cat-cell-${categoryIndex}-${colIndex}`}
                    className={styles.tableCell}>
                    {colIndex === 1 || colIndex === 6 ? (
                      // Peer Group Rank columns - format like "45 / 163"
                      <Skeleton
                        variant="text"
                        width={70}
                        height={16}
                      />
                    ) : (
                      // Value columns - currency/numbers
                      <Skeleton
                        variant="text"
                        width="65%"
                        height={16}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Sub-category Rows */}
              {[...Array(subCategoryCount)].map((_, subIndex) => (
                <div
                  key={`subcat-${categoryIndex}-${subIndex}`}
                  className={`${styles.tableRow} ${styles.subCategoryRow}`}>
                  {/* Sub-category Name (indented) */}
                  <div className={styles.tableCellLabel}>
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={16}
                    />
                  </div>
                  {/* Data columns */}
                  {[...Array(8)].map((_, colIndex) => (
                    <div
                      key={`subcat-cell-${categoryIndex}-${subIndex}-${colIndex}`}
                      className={styles.tableCell}>
                      {colIndex === 1 || colIndex === 6 ? (
                        // Peer Group Rank columns - format like "32 / 163"
                        <Skeleton
                          variant="text"
                          width={70}
                          height={16}
                        />
                      ) : (
                        // Value columns - currency/numbers
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={16}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </MuiPaper>
  );
};

export default SkeletonEcommSalesPerfGrid;
